import { Card, CardProps, Flex, Typography, Box } from "@xcorejs/ui";
import { useLayout } from "xcore";
import { Product, Accessory, ProductOverviewPage, AccessoryValues, ProductValues } from "xcore/types";
import ProductTag from "components/Product/ProductTag";
import { Localized, MergeTypes } from "@robe/core";
import { resolveSrcSet } from "utils";
import { useEffect, useRef, useState } from "react";
import { Skeleton } from "@mui/joy";
import styled from "styled-components";

export type ProductCardProductValuesFields =
  | "images"
  | "discontinuedGlob"
  | "discontinuedReg"
  | "name"
  | "newIcon"
  | "ipCertifiedIcon"
  | "textTag";
export type ProductCardProductValues = Pick<ProductValues, ProductCardProductValuesFields>;

export type ProductCardAccessoryValuesFields = "images" | "discontinuedGlob" | "discontinuedReg" | "name";
export type ProductCardAccessoryValues = Pick<AccessoryValues, ProductCardAccessoryValuesFields>;

export type ProductCardValuesMerged = MergeTypes<ProductCardProductValues, ProductCardAccessoryValues>;
export type ProductCardProductOrAccessory = Product<ProductCardProductValuesFields> | Accessory<ProductCardAccessoryValuesFields>;
export type ProductCardProductOrAccessoryValuesOnly = Pick<
  Product<ProductCardProductValuesFields> | Accessory<ProductCardAccessoryValuesFields>,
  "values"
>;

export type ProductCardPop = Pick<ProductOverviewPage<"thumbnailTitle" | "title" | "thumbImage" | "thumbImageProduct">, "values">;

type ProductCardProps = CardProps &
  (
    | {
        product: ProductCardProductOrAccessoryValuesOnly;
      }
    | {
        pop: ProductCardPop;
      }
  ) & {
    newIconText?: Localized;
  };

const ProductCardStyle = styled(Box)`
  width: 100% !important;
  height: 100% !important;
  border-bottom: none !important;
`;

const ProductCard = (props: ProductCardProps) => {
  const { general, stringify, image, localize, header } = useLayout();
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [showLoading, setShowLoading] = useState<boolean>(false); // Shows debounced loading if image is still being downloaded
  const timeUntilShowLoading = 400; // in ms

  const imgRef = useRef<HTMLImageElement | null>(null);

  // Fix for unaccessible layout values in modals
  const newIconText = props?.newIconText ?? header?.nav?.products?.values?.newIconText;

  const productOrPop = "product" in props ? props.product : props.pop;
  const values = productOrPop.values;

  const discontinued = "images" in values ? values.discontinuedGlob || localize(values.discontinuedReg) : null;
  const newIcon = "newIcon" in values && values.newIcon;
  const ipCertifiedIcon = "ipCertifiedIcon" in values && (values.ipCertifiedIcon as boolean);
  const textTag = "textTag" in values ? localize(values.textTag as Localized) : null;
  const text = !("images" in values) ? localize(values.thumbnailTitle) || stringify(values.title) : null;
  const hasTag = !!(newIcon || ipCertifiedIcon || textTag || discontinued);

  // todo xcc after localized images migrated remove workaround
  const defaultImageResolved =
    "images" in values && values.images ? (values.images?.en?.[0] ? values.images?.en[0] : localize(values.images)?.[0]) : null;

  const defaultImageSrc = defaultImageResolved ? image(defaultImageResolved, { width: 242, height: 242 }) : null;

  // eslint-disable-next-line @typescript-eslint/no-extra-parens
  const imgSrc =
    defaultImageSrc ??
    ("thumbImageProduct" in values && values.thumbImageProduct
      ? image(values.thumbImageProduct)
      : "thumbImage" in values && values.thumbImage
        ? image(values.thumbImage)
        : null);

  // Check on mount if the image is already loaded from cache
  useEffect(() => {
    if (imgRef.current?.complete) {
      setIsImageLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isImageLoading) {
      // Show loading with debounce
      const timeout = setTimeout(() => setShowLoading(true), timeUntilShowLoading);

      return () => clearTimeout(timeout);
    }

    setShowLoading(false); // Do not show loading if image is loaded
  }, [isImageLoading]);

  return (
    <ProductCardStyle>
      <Card
        variant="outline"
        width="100%"
        maxWidth="100%"
        height="100%"
        body={
          <>
            {imgSrc && (
              <>
                {/* Skeleton loading must be separately. If image would be the child of Skeleton, it would fetch the image twice */}
                {showLoading && <Skeleton variant="rectangular" width="100%" height="100%" />}{" "}
                <img
                  ref={imgRef}
                  src={imgSrc}
                  {...resolveSrcSet(imgSrc, defaultImageSrc)}
                  alt={"images" in values ? stringify(values.name) : (text ?? "")}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "center center",
                  }}
                  loading="lazy"
                  onLoad={() => setIsImageLoading(false)}
                />
                <Typography variant="h4" mt="2rem" textAlign="center" wordBreak="break-word" overflow="anywhere">
                  {"images" in values ? stringify(values.name) : text}
                </Typography>
              </>
            )}
            {hasTag && (
              <Flex justifyContent="space-between" width="100%" position="absolute" top="0">
                <Flex>
                  {ipCertifiedIcon && (
                    <ProductTag maxHeight="4rem" variant="ipCertified" marginRight="auto">
                      IP65
                    </ProductTag>
                  )}
                </Flex>
                <Flex flexDirection="column">
                  <>
                    {newIcon && (
                      <ProductTag variant="new" centered>
                        {stringify(newIconText)}
                      </ProductTag>
                    )}
                    {discontinued && (
                      <ProductTag variant="gray" centered>
                        {stringify(general.values.discontinued)}
                      </ProductTag>
                    )}
                    {textTag && (
                      <ProductTag variant="gray" centered>
                        {textTag}
                      </ProductTag>
                    )}
                  </>
                </Flex>
              </Flex>
            )}
          </>
        }
        _body={{
          flexDirection: "column",
          alignItems: "center",
          height: "100%",
          justifyContent: "space-between",
        }}
        _hover={{
          border: "2px solid rgba(69, 86, 99, 0.75)",
        }}
        transition="border 300ms"
        {...props}
      />
    </ProductCardStyle>
  );
};

export default ProductCard;

export const productCardProductFields: ProductCardProductValuesFields[] = [
  "images",
  "discontinuedReg",
  "discontinuedGlob",
  "name",
  "newIcon",
  "ipCertifiedIcon",
  "textTag",
];
